import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
    AdGroupType,
    AvailableAdGroup,
    Destination,
    DestinationMatchType,
    Source,
    availableAdGroups,
} from './mockData'
import {
    MT_LABEL,
    MatchBadge,
    MatchKey,
    NEGATION_KEYS,
    TARGET_KEYS,
    badgeTypeFor,
    canNegate,
    canTarget,
    deriveStatus,
} from './sharedRoutingHelpers'

export interface MatrixModalProps {
    sources: Source[]
    promote: Destination[]
    negate: Destination[]
    /** Override the available ad-group pool. Defaults to the mock data pool. */
    availableAdGroups?: AvailableAdGroup[]
    onSave: (next: { sources: Source[]; promote: Destination[]; negate: Destination[] }) => void
    onClose: () => void
}

interface MatrixRow {
    id: string
    campaign: string
    adGroup: string
    matchType: string
    type: AdGroupType
    status: 'enabled' | 'paused' | 'archived'
    asin?: string
    displayName?: string
}

interface RowFlags {
    source: boolean
    target: Record<MatchKey, boolean>
    negation: Record<MatchKey, boolean>
}

type Struct = Record<string, RowFlags>

const emptyRow = (): RowFlags => ({
    source: false,
    target: { broad: false, phrase: false, exact: false, product: false },
    negation: { broad: false, phrase: false, exact: false, product: false },
})

const PMCheck = ({
    checked,
    indeterminate,
    onChange,
    accent,
}: {
    checked: boolean
    indeterminate?: boolean
    onChange: (v: boolean) => void
    accent?: 'tgt' | 'neg' | 'src'
}) => {
    const on = !!checked || !!indeterminate
    return (
        <label
            className={
                'pm-check' +
                (on ? ' pm-check--on' : '') +
                (indeterminate ? ' pm-check--indeterminate' : '') +
                (accent ? ' pm-check--' + accent : '')
            }
        >
            <input
                type="checkbox"
                checked={!!checked}
                onChange={(e) => onChange(e.target.checked)}
            />
            <span className="pm-check-box">
                {indeterminate ? (
                    <span className="pm-check-dash" />
                ) : (
                    <svg viewBox="0 0 12 12" width="10" height="10">
                        <path
                            d="M2 6 L5 9 L10 3"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                )}
            </span>
        </label>
    )
}

export const MatrixModal = ({
    sources,
    promote,
    negate,
    availableAdGroups: availableAdGroupsProp,
    onSave,
    onClose,
}: MatrixModalProps) => {
    const pool = availableAdGroupsProp ?? availableAdGroups
    // Build the combined ad-group set: anything already referenced + the
    // available pool. Dedupe by id and sort by campaign then ad group name.
    const linked = useMemo<MatrixRow[]>(() => {
        const map = new Map<string, MatrixRow>()
        const add = (
            id: string,
            campaign: string,
            adGroup: string,
            type: AdGroupType,
            status: 'enabled' | 'paused' | 'archived',
            asin?: string,
            displayName?: string,
        ) => {
            if (map.has(id)) return
            // 'TARGET'.toLowerCase() === 'target', but canTarget/canNegate
            // expect 'product' for ASIN-targeting ad groups.
            // See PROMOTION_RULE_BUSINESS_LOGIC.md §6b.
            const matchType = type === 'TARGET' ? 'product' : type.toLowerCase()
            map.set(id, {
                id,
                campaign,
                adGroup,
                matchType,
                type,
                status,
                asin,
                displayName,
            })
        }
        for (const s of sources) {
            add(
                s.id,
                s.campaignName,
                s.adGroupName,
                s.type,
                deriveStatus(s.id),
                s.asin,
                s.displayName,
            )
        }
        for (const d of promote) {
            add(d.id, d.campaignName, d.adGroupName, d.type, deriveStatus(d.id))
        }
        for (const d of negate) {
            add(d.id, d.campaignName, d.adGroupName, d.type, deriveStatus(d.id))
        }
        for (const g of pool) {
            add(g.id, g.campaignName, g.adGroupName, g.type, g.status ?? deriveStatus(g.id), g.asin)
        }
        const arr = [...map.values()]
        arr.sort((a, b) => {
            const c = a.campaign.localeCompare(b.campaign)
            if (c !== 0) return c
            return a.adGroup.localeCompare(b.adGroup)
        })
        return arr
    }, [sources, promote, negate])

    // Reconstruct struct from the three lists.
    const initialStruct = useMemo<Struct>(() => {
        const out: Struct = {}
        for (const g of linked) out[g.id] = emptyRow()
        for (const s of sources) {
            if (!out[s.id]) out[s.id] = emptyRow()
            out[s.id].source = true
        }
        for (const d of promote) {
            if (!out[d.id]) out[d.id] = emptyRow()
            for (const m of d.matchTypes) {
                out[d.id].target[m as MatchKey] = true
            }
        }
        for (const d of negate) {
            if (!out[d.id]) out[d.id] = emptyRow()
            for (const m of d.matchTypes) {
                out[d.id].negation[m as MatchKey] = true
            }
        }
        return out
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const [struct, setStruct] = useState<Struct>(initialStruct)
    const [bulkEditDirty, setBulkEditDirty] = useState(false)
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
    const toggleCampaign = (campaign: string) =>
        setCollapsed((prev) => {
            const next = new Set(prev)
            next.has(campaign) ? next.delete(campaign) : next.add(campaign)
            return next
        })

    const lookup = useMemo(() => {
        const m = new Map<string, MatrixRow>()
        for (const g of linked) m.set(g.id, g)
        return m
    }, [linked])

    // Carry over the original Source/Destination shapes if a row was already
    // in the input lists (preserves things like displayName and matchTypes
    // order).
    const sourceLookup = useMemo(() => {
        const m = new Map<string, Source>()
        for (const s of sources) m.set(s.id, s)
        return m
    }, [sources])

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [onClose])

    const patchRow = (rowId: string, patch: Partial<RowFlags>) => {
        setBulkEditDirty(true)
        setStruct((cur) => ({
            ...cur,
            [rowId]: { ...(cur[rowId] || emptyRow()), ...patch },
        }))
    }
    const setNested = (rowId: string, key: 'target' | 'negation', sub: MatchKey, val: boolean) => {
        setBulkEditDirty(true)
        setStruct((cur) => {
            const row = cur[rowId] || emptyRow()
            return {
                ...cur,
                [rowId]: { ...row, [key]: { ...row[key], [sub]: val } },
            }
        })
    }

    /** State of a column header: all/some/none rows checked among eligible rows. */
    type ColState = { checked: boolean; indeterminate: boolean; eligibleCount: number }
    const colState = (kind: 'source' | 'target' | 'negation', sub?: MatchKey): ColState => {
        let total = 0
        let on = 0
        for (const g of linked) {
            const eligible =
                kind === 'source'
                    ? true
                    : kind === 'target'
                      ? canTarget(g.matchType, sub as MatchKey)
                      : canNegate(g.matchType, sub as MatchKey)
            if (!eligible) continue
            total += 1
            const row = struct[g.id]
            if (!row) continue
            const val =
                kind === 'source'
                    ? row.source
                    : (row[kind] as Record<MatchKey, boolean>)[sub as MatchKey]
            if (val) on += 1
        }
        return {
            eligibleCount: total,
            checked: total > 0 && on === total,
            indeterminate: on > 0 && on < total,
        }
    }

    const setCol = (
        kind: 'source' | 'target' | 'negation',
        sub: MatchKey | undefined,
        val: boolean,
    ) => {
        setBulkEditDirty(true)
        setStruct((cur) => {
            const next: Struct = { ...cur }
            for (const g of linked) {
                const eligible =
                    kind === 'source'
                        ? true
                        : kind === 'target'
                          ? canTarget(g.matchType, sub as MatchKey)
                          : canNegate(g.matchType, sub as MatchKey)
                if (!eligible) continue
                const row = next[g.id] || emptyRow()
                if (kind === 'source') {
                    next[g.id] = { ...row, source: val }
                } else {
                    next[g.id] = {
                        ...row,
                        [kind]: { ...row[kind], [sub as MatchKey]: val },
                    } as RowFlags
                }
            }
            return next
        })
    }

    const toggleCol = (kind: 'source' | 'target' | 'negation', sub?: MatchKey) => {
        const st = colState(kind, sub)
        setCol(kind, sub, !(st.checked && !st.indeterminate))
    }

    const byCampaign = useMemo(() => {
        const m = new Map<string, MatrixRow[]>()
        for (const g of linked) {
            if (!m.has(g.campaign)) m.set(g.campaign, [])
            m.get(g.campaign)!.push(g)
        }
        return [...m.entries()]
    }, [linked])

    const buildNext = () => {
        const nextSources: Source[] = []
        const nextPromote: Destination[] = []
        const nextNegate: Destination[] = []
        for (const g of linked) {
            const s = struct[g.id]
            if (!s) continue
            if (s.source) {
                const existing = sourceLookup.get(g.id)
                nextSources.push(
                    existing ?? {
                        id: g.id,
                        campaignName: g.campaign,
                        adGroupName: g.adGroup,
                        type: g.type,
                        ...(g.asin ? { asin: g.asin } : {}),
                        ...(g.displayName ? { displayName: g.displayName } : {}),
                    },
                )
            }
            const targets = (Object.keys(s.target) as MatchKey[]).filter((k) => s.target[k])
            if (targets.length > 0) {
                nextPromote.push({
                    id: g.id,
                    campaignName: g.campaign,
                    adGroupName: g.adGroup,
                    type: g.type,
                    matchTypes: targets as DestinationMatchType[],
                })
            }
            const negs = (Object.keys(s.negation) as MatchKey[]).filter((k) => s.negation[k])
            if (negs.length > 0) {
                nextNegate.push({
                    id: g.id,
                    campaignName: g.campaign,
                    adGroupName: g.adGroup,
                    type: g.type,
                    matchTypes: negs as DestinationMatchType[],
                })
            }
        }
        return { sources: nextSources, promote: nextPromote, negate: nextNegate }
    }

    // Propagate changes immediately as the user toggles checkboxes.
    const isFirstRender = useRef(true)
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }
        onSave(buildNext())
    }, [struct])

    const content = (
        <div className="routing-cards-root">
            {/* Scrim */}
            <div
                className="pm-picker-scrim"
                style={{ alignItems: 'stretch', justifyContent: 'flex-end', padding: 0 }}
                onClick={onClose}
            >
                <div
                    className="pm-matrix-modal"
                    style={{
                        width: 'min(1060px, 96vw)',
                        maxHeight: '100vh',
                        height: '100vh',
                        borderRadius: '18px 0 0 18px',
                        borderRight: 'none',
                        borderLeft: '1px solid rgba(15,23,42,0.06)',
                        boxShadow: '-8px 0 32px rgba(15,23,42,0.10), 0 8px 40px rgba(15,23,42,.12)',
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="pm-matrix-head">
                        <div>
                            <div className="pm-matrix-title">Edit routing</div>
                            <div className="pm-matrix-sub">
                                Map any ad group to sources, targets, and negations at once.
                            </div>
                        </div>
                        <button type="button" className="pm-matrix-back-btn" onClick={onClose}>
                            Done
                        </button>
                    </div>
                    {bulkEditDirty && (
                        <div className="pm-matrix-notice" role="status">
                            Your changes are ready. Close this panel, then save the Smart Campaign.
                        </div>
                    )}
                    <div className="pm-matrix-body">
                        <div className="pm pm--compact">
                            <div className="pm-scroll">
                                <table className="pm-table">
                                    <colgroup>
                                        <col className="pm-col-name" />
                                        <col className="pm-col-src" />
                                        {TARGET_KEYS.map((k) => (
                                            <col key={'ct' + k} className="pm-col-tgt" />
                                        ))}
                                        {NEGATION_KEYS.map((k) => (
                                            <col key={'cn' + k} className="pm-col-neg" />
                                        ))}
                                    </colgroup>
                                    <thead>
                                        <tr className="pm-head-groups">
                                            <th></th>
                                            <th className="pm-h-src">
                                                <div className="pm-h-title">Source</div>
                                            </th>
                                            <th colSpan={4} className="pm-h-tgt">
                                                <div className="pm-h-title">Promote</div>
                                            </th>
                                            <th colSpan={3} className="pm-h-neg">
                                                <div className="pm-h-title">Negate</div>
                                            </th>
                                        </tr>
                                        <tr className="pm-head-cols">
                                            <th>
                                                <span className="pm-all-label">Ad group</span>
                                            </th>
                                            <th className="pm-headcol-src">
                                                <div className="pm-headcol">
                                                    <PMCheck
                                                        checked={colState('source').checked}
                                                        indeterminate={
                                                            colState('source').indeterminate
                                                        }
                                                        onChange={() => toggleCol('source')}
                                                        accent="src"
                                                    />
                                                    <span className="pm-headcol-label">Source</span>
                                                </div>
                                            </th>
                                            {TARGET_KEYS.map((k) => {
                                                const st = colState('target', k)
                                                return (
                                                    <th key={'t' + k} className="pm-headcol-tgt">
                                                        <div className="pm-headcol">
                                                            <PMCheck
                                                                checked={st.checked}
                                                                indeterminate={st.indeterminate}
                                                                onChange={() =>
                                                                    toggleCol('target', k)
                                                                }
                                                                accent="tgt"
                                                            />
                                                            <span className="pm-headcol-label">
                                                                {MT_LABEL[k]}
                                                            </span>
                                                        </div>
                                                    </th>
                                                )
                                            })}
                                            {NEGATION_KEYS.map((k) => {
                                                const st = colState('negation', k)
                                                return (
                                                    <th key={'n' + k} className="pm-headcol-neg">
                                                        <div className="pm-headcol">
                                                            <PMCheck
                                                                checked={st.checked}
                                                                indeterminate={st.indeterminate}
                                                                onChange={() =>
                                                                    toggleCol('negation', k)
                                                                }
                                                                accent="neg"
                                                            />
                                                            <span className="pm-headcol-label">
                                                                {MT_LABEL[k]}
                                                            </span>
                                                        </div>
                                                    </th>
                                                )
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {byCampaign.map(([campaign, rows]) => {
                                            const isCollapsed = collapsed.has(campaign)
                                            return (
                                                <React.Fragment key={campaign}>
                                                    <tr
                                                        className="pm-campaign-row"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => toggleCampaign(campaign)}
                                                    >
                                                        <td colSpan={9}>
                                                            <div
                                                                className="pm-campaign-name"
                                                                title={campaign}
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 6,
                                                                }}
                                                            >
                                                                <svg
                                                                    width="10"
                                                                    height="10"
                                                                    viewBox="0 0 10 10"
                                                                    fill="none"
                                                                    style={{
                                                                        flexShrink: 0,
                                                                        transition:
                                                                            'transform 0.15s',
                                                                        transform: isCollapsed
                                                                            ? 'rotate(-90deg)'
                                                                            : 'rotate(0deg)',
                                                                    }}
                                                                >
                                                                    <path
                                                                        d="M2 3.5l3 3 3-3"
                                                                        stroke="currentColor"
                                                                        strokeWidth="1.25"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                </svg>
                                                                {campaign}
                                                                <span
                                                                    style={{
                                                                        marginLeft: 4,
                                                                        fontSize: 11,
                                                                        opacity: 0.5,
                                                                    }}
                                                                >
                                                                    {rows.length}
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    {!isCollapsed &&
                                                        rows.map((g) => {
                                                            const s = struct[g.id] || emptyRow()
                                                            return (
                                                                <tr
                                                                    key={g.id}
                                                                    className={
                                                                        'pm-row' +
                                                                        (s.source
                                                                            ? ' pm-row--src'
                                                                            : '')
                                                                    }
                                                                >
                                                                    <td className="pm-cell-name">
                                                                        <span
                                                                            className={
                                                                                'status-dot status-dot--' +
                                                                                g.status
                                                                            }
                                                                        />
                                                                        <span className="pm-name-text">
                                                                            {g.adGroup}
                                                                        </span>
                                                                        <MatchBadge
                                                                            type={badgeTypeFor(
                                                                                g.type,
                                                                            )}
                                                                        />
                                                                    </td>
                                                                    <td className="pm-cell pm-cell--src">
                                                                        <PMCheck
                                                                            checked={s.source}
                                                                            onChange={(v) =>
                                                                                patchRow(g.id, {
                                                                                    source: v,
                                                                                })
                                                                            }
                                                                        />
                                                                    </td>
                                                                    {TARGET_KEYS.map((k) => {
                                                                        const ok = canTarget(
                                                                            g.matchType,
                                                                            k,
                                                                        )
                                                                        return (
                                                                            <td
                                                                                key={'t' + k}
                                                                                className={
                                                                                    'pm-cell pm-cell--tgt' +
                                                                                    (!ok
                                                                                        ? ' pm-cell--off'
                                                                                        : '')
                                                                                }
                                                                            >
                                                                                {ok ? (
                                                                                    <PMCheck
                                                                                        checked={
                                                                                            !!s
                                                                                                .target?.[
                                                                                                k
                                                                                            ]
                                                                                        }
                                                                                        onChange={(
                                                                                            v,
                                                                                        ) =>
                                                                                            setNested(
                                                                                                g.id,
                                                                                                'target',
                                                                                                k,
                                                                                                v,
                                                                                            )
                                                                                        }
                                                                                        accent="tgt"
                                                                                    />
                                                                                ) : null}
                                                                            </td>
                                                                        )
                                                                    })}
                                                                    {NEGATION_KEYS.map((k) => {
                                                                        const ok = canNegate(
                                                                            g.matchType,
                                                                            k,
                                                                        )
                                                                        return (
                                                                            <td
                                                                                key={'n' + k}
                                                                                className={
                                                                                    'pm-cell pm-cell--neg' +
                                                                                    (!ok
                                                                                        ? ' pm-cell--off'
                                                                                        : '')
                                                                                }
                                                                            >
                                                                                {ok ? (
                                                                                    <PMCheck
                                                                                        checked={
                                                                                            !!s
                                                                                                .negation?.[
                                                                                                k
                                                                                            ]
                                                                                        }
                                                                                        onChange={(
                                                                                            v,
                                                                                        ) =>
                                                                                            setNested(
                                                                                                g.id,
                                                                                                'negation',
                                                                                                k,
                                                                                                v,
                                                                                            )
                                                                                        }
                                                                                        accent="neg"
                                                                                    />
                                                                                ) : null}
                                                                            </td>
                                                                        )
                                                                    })}
                                                                </tr>
                                                            )
                                                        })}
                                                </React.Fragment>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )

    if (typeof document === 'undefined') return null
    return createPortal(content, document.body)
}

export const BulkEditIcon = () => (
    <svg viewBox="0 0 14 14" width="12" height="12" aria-hidden="true">
        <rect
            x="1.5"
            y="1.5"
            width="4"
            height="4"
            stroke="currentColor"
            strokeWidth="1.2"
            fill="none"
        />
        <rect
            x="8.5"
            y="1.5"
            width="4"
            height="4"
            stroke="currentColor"
            strokeWidth="1.2"
            fill="none"
        />
        <rect
            x="1.5"
            y="8.5"
            width="4"
            height="4"
            stroke="currentColor"
            strokeWidth="1.2"
            fill="none"
        />
        <rect
            x="8.5"
            y="8.5"
            width="4"
            height="4"
            stroke="currentColor"
            strokeWidth="1.2"
            fill="none"
        />
    </svg>
)
